import React from 'react'
import { NavLink } from 'react-router-dom'
import { House, Layers, MessageSquare, Calendar, Phone, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/uiStore'
import { useCallStore } from '@/stores/callStore'
import { MobileDockShell } from '@/components/ui/MobileDockShell'
import { motion, MotionConfig, useReducedMotion } from 'motion/react'
import { MORPH_SPRING, DOCK_SURFACE_ID, PILL_RADIUS, DOCK_PILL_H } from './dockMorph'
import { DOCK_TAB_CAPABILITY } from './navItems'
import { usePermissions } from '@/hooks/usePermission'
import { DockCreateMenu } from './CreateMenu'

// Dock order (owner decision, docs/product/ux-decisions.md §21): Work · Calendar ·
// Home · Inbox · Phone. Calendar replaced Customers so the crew's "where am I
// going today" is one tap; Contacts is in More, search and the + menu.
// Home is index 2 (center of 5) — the More notch is anchored to its position.
export const NAV_TABS = [
  { label: 'Work',      path: '/work',      icon: Layers },
  { label: 'Calendar',  path: '/calendar',  icon: Calendar },
  { label: 'Home',      path: '/dashboard', icon: House },
  { label: 'Inbox',     path: '/inbox',     icon: MessageSquare },
] as const

function TabItem({
  icon: Icon,
  label,
  isActive,
  dot = false,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  isActive: boolean
  /** Unread marker (Inbox). White: "needs you", same signal as the sidebar badge. */
  dot?: boolean
}) {
  return (
    <>
      <span
        className={cn(
          'relative flex items-center justify-center w-14 h-8 rounded-full transition-all duration-200',
          isActive ? 'bg-white/10' : 'bg-transparent',
        )}
      >
        <Icon
          className={cn(
            'w-[26px] h-[26px] transition-colors duration-200',
            isActive ? 'text-white' : 'text-white/[0.62]',
          )}
        />
        {dot && (
          <span
            data-testid="dock-unread-dot"
            className="absolute top-0.5 right-3 w-2 h-2 rounded-full bg-white shadow-[0_0_0_2px_#0a0a0c]"
          />
        )}
      </span>
      <span
        className={cn(
          'text-[12px] font-semibold leading-none tracking-tight transition-colors duration-200',
          isActive ? 'text-white' : 'text-white/[0.62]',
        )}
      >
        {label}
      </span>
    </>
  )
}

// Subtle dome — wide and short so it reads as a gentle indicator, not a peak.
// Tangent-continuous at endpoints and apex.
// Sized for the 62px pill: a visible bump, not a 6px hairline.
const HUMP_W = 116
const HUMP_H = 16

/**
 * Tap target for the More dome, kept separate from the dome's own width.
 *
 * The two were the same number, which is how a 96px-wide invisible button
 * ended up floating over page content: the dome is a 96×6 painted arc, but
 * the button inherited that width and added 44px of height above the dock.
 */
// Target matches the bump's paint (plus 2px into the pill edge).
const MORE_TAP_W = 48 // MTB-1: capped at 48 so it cannot sprawl over content
const MORE_TAP_H = HUMP_H + 2
// Fill: closed path so the dome has a solid background
const HUMP_FILL_D = `M 0 ${HUMP_H} C ${HUMP_W * 0.25} ${HUMP_H} ${HUMP_W * 0.33} 0 ${HUMP_W / 2} 0 C ${HUMP_W * 0.67} 0 ${HUMP_W * 0.75} ${HUMP_H} ${HUMP_W} ${HUMP_H} Z`
// Border: open path — arc only, no bottom edge (pill border provides that)
const HUMP_BORDER_D = `M 0 ${HUMP_H} C ${HUMP_W * 0.25} ${HUMP_H} ${HUMP_W * 0.33} 0 ${HUMP_W / 2} 0 C ${HUMP_W * 0.67} 0 ${HUMP_W * 0.75} ${HUMP_H} ${HUMP_W} ${HUMP_H}`

// Pill surface tokens — single source of truth so the dome and pill match.
/**
 * Dock look. 'drawer' = full-width solid bar flush to the screen bottom and
 * sides with rounded top corners; 'pill' = the floating capsule. Flip this
 * one constant to switch back.
 */
export const DOCK_STYLE = 'pill' as 'drawer' | 'pill'
const IS_DRAWER = DOCK_STYLE === 'drawer'

// Gap the pill keeps above the screen bottom; the drawer pads its content by
// the same amount, so icons and labels sit in the same place in both looks
// and the total height (62 + gap = 68px on iPhone) is identical.
export const DOCK_GAP = 'max(6px, calc(var(--mobile-dock-safe-bottom, 0px) - 28px))'
/** Height of the pill row. */
export { DOCK_PILL_H }

// Surface tokens — single source of truth so the dome and bar match.
const DRAWER_TOP = '#1C1C21' // solid; the dome uses it so it reads as one piece
const DOCK_BG = IS_DRAWER ? DRAWER_TOP : 'rgba(10,10,12,0.88)'
const DOCK_BORDER = IS_DRAWER ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.10)'
const DRAWER_RADIUS = 26

/** Tab content fades + shrinks out while the pill is morphed into the card. */
function TabFade({ hidden, children }: { hidden: boolean; children: React.ReactNode }) {
  const reduceMotion = useReducedMotion()
  return (
    <motion.span
      className="flex flex-col items-center justify-center gap-[4px]"
      initial={false}
      animate={hidden ? { opacity: 0, scale: 0.8, y: -6 } : { opacity: 1, scale: 1, y: 0 }}
      transition={reduceMotion ? { duration: 0 } : hidden ? { duration: 0.14 } : { ...MORPH_SPRING, delay: 0.08 }}
    >
      {children}
    </motion.span>
  )
}

export function BottomTabBar() {
  const { setMoreMenuOpen, moreMenuOpen, inboxBadge, searchOpen } = useUIStore()
  const softphoneExpanded = useCallStore((s) => s.softphoneExpanded)
  const reduceMotion = useReducedMotion()
  const setSoftphoneExpanded = useCallStore((s) => s.setSoftphoneExpanded)
  // Tabs the person can open (F1 capabilities). Order is the WORK lane's call.
  const { can } = usePermissions()
  const tabs = NAV_TABS.filter((t) => { const cap = DOCK_TAB_CAPABILITY[t.path]; return !cap || can(cap) })

  // Search is full screen on a phone; the dock is portaled to <body> and
  // would otherwise sit on top of its last results.
  if (searchOpen) return null

  return (
    // md:hidden = phones only (<768px). From 768px up the sidebar is the one
    // navigation (UX-133): tablets used to get the sidebar AND this dock, with
    // the dock covering the sidebar's account row and the bottom of the page.
    // insetTarget="children" — MobileDockShell exposes the safe-area inset as
    // --mobile-dock-safe-bottom; the pill consumes it via paddingBottom so the
    // pill's surface extends through the inset zone, flush with the screen
    // bottom (no visible gap below the dock on iOS).
    // insetTarget="children" — shell exposes the safe-area-inset-bottom value
    // as --mobile-dock-safe-bottom but does NOT pad itself, so we can choose
    // exactly how much of the inset to leave as breathing room below the pill.
    <MobileDockShell
      className="md:hidden"
      insetTarget="children"
      reserveSpace
      gutterX={IS_DRAWER ? 0 : 10}
      style={IS_DRAWER ? undefined : {
        // Pill bottom edge sits (inset - 28px) above the viewport bottom, i.e.
        // ~6px on iPhone (inset ≈ 34px), hugging the home indicator. Floor of
        // 6px keeps a gap on devices with no inset. Pill (62) + gap (6) = 68px,
        // under the previous 48 + 22 = 70px.
        paddingBottom: DOCK_GAP,
      }}
    >
      <MotionConfig reducedMotion="user">
      {/* One row: the tab pill, then the "+" on the same baseline (UX-111).
          The "+" used to float ~70px above the dock's right end, over page
          content, so on every page it sat on something at rest: a card's
          service chip, "New opportunity", a settings switch, a row's call
          icon. In the dock row it only ever covers the band the dock already
          reserves (--dock-h), and pages already pad clear of that. */}
      <div className="flex items-center gap-2">
      {/* Relative wrapper — provides positioning context for the More notch */}
      <div className="relative flex-1 min-w-0">

        {/* ── Pill nav — fully rounded capsule ─────────────────── */}
        {/* All four corners rounded (capsule). The pill floats just above
            the iOS home indicator. With viewport-fit=cover, the safe-area
            inset zone below the pill is painted by the page background
            (#000 midnight) — same color as the rest of the screen, so no
            visible "black strip" beyond the natural dark theme. */}
        {/* Pill surface — shares layoutId with the More card, so opening
            More morphs this exact shape (size + radius on a spring) up into
            the card, and closing morphs it back. */}
        {!IS_DRAWER && !moreMenuOpen && (
          <motion.div
            layoutId={DOCK_SURFACE_ID}
            aria-hidden="true"
            className="absolute inset-0"
            // MotionConfig "user" doesn't stop a shared-layout spring; cut it
            // under reduced motion like MoreMenuSheet does (UX-116).
            transition={reduceMotion ? { duration: 0 } : MORPH_SPRING}
            style={{
              borderRadius: PILL_RADIUS,
              background: DOCK_BG,
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              border: `0.5px solid ${DOCK_BORDER}`,
              boxShadow: '0 24px 64px -12px rgba(0,0,0,0.85), 0 4px 16px rgba(0,0,0,0.5)',
            }}
          />
        )}
        <nav
          className={cn('flex items-stretch', !IS_DRAWER && 'h-[62px]')}
          style={IS_DRAWER ? {
            // Drawer: flush to bottom and sides, solid, rounded top only.
            // Height = the pill's 62px row + the same gap the pill kept below
            // it, so it is never taller than the pill dock was.
            height: `calc(62px + ${DOCK_GAP})`,
            paddingBottom: DOCK_GAP,
            borderRadius: `${DRAWER_RADIUS}px ${DRAWER_RADIUS}px 0 0`,
            background: `linear-gradient(180deg, ${DRAWER_TOP} 0%, #121215 100%)`,
            boxShadow: `inset 0 1px 0 ${DOCK_BORDER}, 0 -10px 28px -6px rgba(0,0,0,0.7), 0 -2px 6px rgba(0,0,0,0.45)`,
          } : {
            // Pill: the surface is the shared-layout <motion.div> below, so
            // it can morph into the More card. The nav row stays transparent.
            position: 'relative',
            borderRadius: PILL_RADIUS,
          }}
        >
          {tabs.map(({ label, path, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              className="flex-1 flex flex-col items-center justify-center gap-[4px] pb-1"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {({ isActive }) => (
                <TabFade hidden={moreMenuOpen}>
                  <TabItem icon={Icon} label={label} isActive={isActive} dot={path === '/inbox' && inboxBadge > 0} />
                </TabFade>
              )}
            </NavLink>
          ))}

          {/* Phone — 5th button; opens the softphone panel */}
          <button
            type="button"
            onClick={() => setSoftphoneExpanded(!softphoneExpanded)}
            aria-label={softphoneExpanded ? 'Close phone' : 'Open phone'}
            aria-expanded={softphoneExpanded}
            className="flex-1 flex flex-col items-center justify-center gap-[4px] pb-1"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <TabFade hidden={moreMenuOpen}>
              <TabItem icon={Phone} label="Phone" isActive={softphoneExpanded} />
            </TabFade>
          </button>
        </nav>

        {/* ── More notch — subtle dome ─────────────────────────── */}
        {/* Visual: SVG cubic-bezier arc above Dashboard (center, left: 50%) */}
        <div
          aria-hidden="true"
          className="absolute bottom-full left-1/2 -translate-x-1/2 pointer-events-none"
          style={{
            width: HUMP_W, height: HUMP_H, marginBottom: -1,
            opacity: moreMenuOpen ? 0 : 1,
            transition: 'opacity 160ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <svg
            width={HUMP_W}
            height={HUMP_H}
            viewBox={`0 0 ${HUMP_W} ${HUMP_H}`}
            style={{ display: 'block' }}
          >
            {/* Glass fill — matches pill background */}
            <path d={HUMP_FILL_D} fill={DOCK_BG} />
            {/* Hairline border on the arc only (pill border closes the bottom edge) */}
            <path
              d={HUMP_BORDER_D}
              fill="none"
              stroke={DOCK_BORDER}
              strokeWidth="0.5"
            />
          </svg>
        </div>

        {/* Tap target.

            This was 96×44 — a fully transparent rectangle, anchored
            `bottom-full` so all 44px of it sat ABOVE the dock, over page
            content, with an 8×8 glyph as its only visible affordance. It
            stole taps from whatever was underneath on every mobile route. A
            browser sweep confirmed it by clicking: on /marketing/social the
            "Posts" tab centre resolved to this button via elementFromPoint,
            so tapping Posts opened the More sheet and the tab could not be
            reached at all. Dashboard tiles, contact rows, an inbox thread, a
            calendar event and a settings input were all measured sitting
            underneath it too.

            Now sized to what it actually shows: MORE_TAP_W wide (the a11y
            minimum) instead of the dome's full 96, and short enough that it
            no longer reaches the row of content immediately above the dock.
            Area over page content drops from 96×38 to 44×22 — about an 84%
            reduction — and the glyph is 16px rather than 8px, so the
            affordance is visible enough to aim at.

            The height is deliberately under the 44px ideal. That is a real
            trade-off and it goes this way round because an invisible target
            that swallows a neighbouring control's taps is the worse failure —
            an unreachable tab is a total loss, a slightly small button is a
            harder tap. The clean answer is a taller, visibly-domed affordance
            whose target matches its paint, but that changes how the dock
            looks, which is Gaige's call rather than a bug fix. */}
        <button
          type="button"
          onClick={() => setMoreMenuOpen(!moreMenuOpen)}
          aria-label="More navigation"
          aria-expanded={moreMenuOpen}
          data-testid="more-nav-button"
          className={cn(
            'absolute bottom-full left-1/2 -translate-x-1/2',
            'flex items-center justify-center pt-[3px]',
            'transition-colors duration-150',
            moreMenuOpen ? 'text-white' : 'text-white/[0.62]',
          )}
          style={{
            width: MORE_TAP_W,
            height: MORE_TAP_H,
            marginBottom: -2,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <MoreHorizontal className="w-5 h-5" />
        </button>

      </div>
      <DockCreateMenu />
      </div>
      </MotionConfig>
    </MobileDockShell>
  )
}
